import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingFarmResourceConsumption,
  useMutateFootprintingFarmResourceConsumption,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InformationCard, InputNumber, PageSpin } from "components";

interface FarmFuelProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const FarmFuelForm = ({ primaryDataId, readOnly, farmId, year }: FarmFuelProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [dieselL, setDieselL] = useState<number | null>(null);
  const [petrolL, setPetrolL] = useState<number | null>(null);
  const [naturalGasM3, setNautralGasM3] = useState<number | null>(null);
  const [heatingOilL, setHeatingOilL] = useState<number | null>(null);

  const farmResourceConsumptionQuery = useGetFootprintingFarmResourceConsumption(primaryDataId ?? "");
  const { mutate: mutateFarmResourceConsumption } = useMutateFootprintingFarmResourceConsumption(
    farmId,
    year,
    {
      id: farmResourceConsumptionQuery.data?.id,
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      dieselL,
      petrolL,
      naturalGasM3,
      heatingOilL,
    },
    primaryDataId
  );

  const isLoading = queryIsLoading(farmResourceConsumptionQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFarmResourceConsumption();
      }
    },
    [isLoading, mutateFarmResourceConsumption, readOnly]
  );

  useEffect(() => {
    if (farmResourceConsumptionQuery.data) {
      setDieselL(farmResourceConsumptionQuery.data.dieselL ?? null);
      setPetrolL(farmResourceConsumptionQuery.data.petrolL ?? null);
      setNautralGasM3(farmResourceConsumptionQuery.data.naturalGasM3 ?? null);
      setHeatingOilL(farmResourceConsumptionQuery.data.heatingOilL ?? null);

      formRef.current?.setFieldsValue({
        dieselL: farmResourceConsumptionQuery.data.dieselL ?? null,
        petrolL: farmResourceConsumptionQuery.data.petrolL ?? null,
        naturalGasM3: farmResourceConsumptionQuery.data.naturalGasM3 ?? null,
        heatingOilL: farmResourceConsumptionQuery.data.heatingOilL ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["dieselL", "petrolL", "naturalGasM3", "heatingOilL"] as const).some(
        (field) => farmResourceConsumptionQuery.data[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [farmResourceConsumptionQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-300 padding-right-300">{t("fuelForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("fuelForm.info")}</p>
        </Flex>
      </Card>

      <Card dataTestId="footprinting-farm-fuel-form" className="margin-bottom-100 padding-200">
        <InformationCard
          iconName=""
          showIcon={false}
          text={t("fuelForm.subText")}
          title={""}
          variant={"neutral"}
          className="margin-bottom-100 sub-text-information-card"
        />
        <Form ref={formRef}>
          <Form.Item className="padding-bottom-100">
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-diesel-input"
              disabled={readOnly}
              name="dieselL"
              label={t("fuelForm.question.dieselQuestionLabel")}
              tooltip={t("fuelForm.tooltips.dieselQuestionLabel")}
              suffix="L"
              value={dieselL}
              initialValue={dieselL}
              onChange={(v) => setDieselL(v)}
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
          <Form.Item className="padding-bottom-100">
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-petrol-input"
              name="petrolL"
              disabled={readOnly}
              label={t("fuelForm.question.petrolQuestionLabel")}
              tooltip={t("fuelForm.tooltips.petrolQuestionLabel")}
              suffix="L"
              value={petrolL}
              initialValue={petrolL}
              onChange={(v) => setPetrolL(v)}
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
          <Form.Item className="padding-bottom-100">
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-gas-input"
              disabled={readOnly}
              name="naturalGasM3"
              label={t("fuelForm.question.naturalGasQuestionLabel")}
              tooltip={t("fuelForm.tooltips.naturalGasQuestionLabel")}
              suffix="m³"
              value={naturalGasM3}
              initialValue={naturalGasM3}
              onChange={(v) => setNautralGasM3(v)}
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-oil-input"
              name="heatingOilL"
              disabled={readOnly}
              label={t("fuelForm.question.heatingOilQuestionLabel")}
              tooltip={t("fuelForm.tooltips.heatingOilQuestionLabel")}
              suffix="L"
              value={heatingOilL}
              initialValue={heatingOilL}
              onChange={(v) => setHeatingOilL(v)}
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
